import React, { useEffect, useState } from 'react';
import { Link } from 'gatsby';
import { X } from 'react-feather';
import {
	hasGPC,
	hasConsent,
	isDismissedThisSession,
	dismissForSession,
	grantConsent
} from './consent';

/**
 * Consent banner (CA privacy hardening).
 *
 * - Compact white card, bottom-right on desktop; bottom sheet on mobile.
 * - Single brand-colored Accept button + small ✕ (no visible decline).
 * - ✕ hides it for the session only (sessionStorage); trackers never load
 *   without Accept. Banner returns on the next visit.
 * - GPC visitors and visitors who already accepted never see it.
 * - Renders null until after mount (SSR/hydration safety).
 */
const ConsentBanner = () => {
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (!hasGPC() && !hasConsent() && !isDismissedThisSession()) {
			setVisible(true);
		}
	}, []);

	if (!visible) return null;

	return (
		<div
			className="fixed bottom-0 left-0 right-0 z-[60] p-3 sm:bottom-4 sm:right-4 sm:left-auto sm:p-0 sm:max-w-[340px]"
			role="dialog"
			aria-label="Cookie consent"
		>
			<div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4">
				<div className="flex items-start justify-between">
					<p className="m-0 text-sm text-gray-700 pr-2">
						<span className="font-semibold text-gray-900">
							We value your privacy.
						</span>{' '}
						We use cookies to analyze site traffic and measure our
						advertising.{' '}
						<Link
							to="/privacy-policy/"
							className="underline text-brand-700 hover:text-brand-800"
						>
							Privacy Policy
						</Link>
					</p>
					<button
						type="button"
						aria-label="Close"
						onClick={() => {
							dismissForSession();
							setVisible(false);
						}}
						className="flex-shrink-0 p-1 -m-1 text-gray-400 hover:text-gray-600 bg-transparent border-0 cursor-pointer"
					>
						<X size={16} />
					</button>
				</div>
				<button
					type="button"
					onClick={() => {
						grantConsent();
						setVisible(false);
					}}
					className="mt-3 w-full cursor-pointer font-semibold text-sm px-4 py-2.5 rounded-2xl text-white border-0 transition duration-150 ease-in-out bg-brand-600 hover:bg-brand-700 focus:bg-brand-700 focus:outline-none"
				>
					Accept
				</button>
			</div>
		</div>
	);
};

export default ConsentBanner;
